import React, { useEffect, useState } from "react"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import _ from "lodash"
import uuidV4 from "uuid/v4"

import keyService, * as chord from "data/key"
import automationService from "data/automation"
import { setlocal } from 'data/lib/local-json'
import { Colors } from "styles/app-styles"
import { Subject } from "rxjs"

// cell and editing (mode) observables
type Cell = {
  row: number
  column: number
  init: boolean
}

type CellVisibility = {
  x_visible: boolean
  y_visible: boolean
  x_offset: number
  y_offset: number
  x_scrollable: Element
  y_scrollable: Element
  cell_rect: ClientRect
}

type ITableAutomation = {
  tableId: string
  resource?: string
  automationActive?: boolean
  tabOut?: Function
  shiftTabOut?: Function
}

enum Direction {
  Up = 0,
  Down,
  Left,
  Right
}

const automationid = uuidV4()
const cell = new BehaviorSubject<Cell>({row: 0, column: 0, init: true})
const editing = new BehaviorSubject<boolean>(false)
const rowContextStream = new Subject<number>()

const setContext = (rowData) => {
  rowContextStream.next(rowData)
}

const getRows = (tableId: string) => {
  const tableSelector = `#${tableId}`
  let rows

  const frozenRows = document.querySelectorAll(`${tableSelector} .p-datatable-frozen-view .p-datatable-tbody > tr`)
  if(frozenRows.length) {
    const unfrozenRows = document.querySelectorAll(`${tableSelector} .p-datatable-unfrozen-view .p-datatable-tbody > tr`)
    rows = _.map(unfrozenRows, (row, i) => _.concat(_.get(frozenRows, i), row))
  } else {
    rows = _.map(document.querySelectorAll(`${tableSelector} .p-datatable-scrollable-body .p-datatable-tbody > tr`), row => [row])
  }

  return rows
}

const getCells = (rows: []) => {
  const cells = _.map(rows, row => {
    return _.reduce(row, (cells, row) => {
      if(_.includes(_.get(row, "classList"), "systemImmutable-row")) {
        let tmpCells = _.concat(cells, Array.from(row.querySelectorAll(`td.system-immutable-editable`)))
        return tmpCells
      } else {
        let tmpCells = _.concat(cells, Array.from(row.querySelectorAll(`td.p-selection-column, td.p-editable-column`)))
        return _.filter(tmpCells, c => !(_.includes(_.get(c, "classList"), "system-disabled") && _.includes(_.get(row, "classList"), "system-row")))
      }
    }, [])
  })
  return cells
}

const getSystemDisabledCells = (tableId: string) => {
  const tableSelector = `#${tableId}`
  const systemDisabledCells = document.querySelectorAll(`${tableSelector} td.system-disabled`)
  return systemDisabledCells
}

const cell_clicked = (tableId, e) => {
  if(isFilterField(e.target)) {
    return false
  }

  const table = document.querySelector(`#${tableId}`)
  const path = e.path || (e.composedPath && e.composedPath())

  if(table && table.contains(e.target)) return true
  else if(table && path && path.length) {
    for(let i = 0; i < path.length; i++) {
      if(table.contains(path[i])) return true
      else if(i >= 3) break
      else continue
    }
  } else return false
  return false
}

const isFilterField = (ele: HTMLElement | null, count: number = 0): boolean => {
  if(!ele) return false
  else if(count > 6) return false
  else if(_.includes(_.get(ele, "className"), "p-filter-column")) return true
  else return isFilterField(ele.parentElement, count++)
}

const cell_visibility = (tableId, c): CellVisibility => {
  const tbodySelector = `#${tableId} .p-datatable-scrollable-body`
  const tbodyParts = document.querySelectorAll(tbodySelector)
  const cellBounds = _.invoke(c, "getBoundingClientRect")

  let visibility = {
    x_visible: true,
    y_visible: true,
    x_offset: cellBounds.x,
    y_offset: cellBounds.y,
    x_scrollable: document.createElement("element"),
    y_scrollable: document.createElement("element"),
    cell_rect: cellBounds
  }

  _.each(tbodyParts, (tbody, i) => {
    if(tbody.contains(c)) {
      const scrollableYSelector = `${tableId}-scrollable-container`
      const paginator = document.querySelectorAll('.p-paginator')[0]
      const paginatorBounds = _.invoke(paginator, "getBoundingClientRect")
      const topPanel = document.querySelector('.top-panel')
      const topPanelBounds = _.invoke(topPanel, "getBoundingClientRect") || {}
      const header = document.querySelectorAll('.p-datatable-scrollable-header-table')[0]
      const headerBounds = _.invoke(header, "getBoundingClientRect")
      const scrollableYElement = document.getElementById(scrollableYSelector)
      const xScrollBounds = _.invoke(tbody, "getBoundingClientRect")
      const visibleBounds = _.invoke(scrollableYElement, "getBoundingClientRect")
      const viewport = _.get(window, "visualViewport")
      const yBound = Math.min(paginatorBounds.top, topPanelBounds.bottom || paginatorBounds.top)

      if(cellBounds.top >= headerBounds.bottom &&
         cellBounds.bottom <= yBound) {
        visibility.y_visible = true
      } else {
        visibility.y_visible = false
        visibility.y_offset = Math.max(cellBounds.top - headerBounds.bottom, cellBounds.bottom - yBound)
        if (cellBounds.y < 200) visibility.y_offset =visibility.y_offset - 37
        if(scrollableYElement) visibility.y_scrollable = scrollableYElement
      }
      if(cellBounds.left >= (i > 0 ? xScrollBounds.left : visibleBounds?.left) &&
         cellBounds.right <= visibleBounds?.right &&
         cellBounds.right <= viewport.width &&
         cellBounds.left >= 75) {
        visibility.x_visible = true
      } else {
        visibility.x_visible = false
        visibility.x_offset = cellBounds.x - xScrollBounds.x
        visibility.x_scrollable = tbody
      }
    }
  })

  return visibility
}

const findRecordId = (ele) => {
  if(ele?.classList) {
    const recordId = _.chain(ele.classList).find(i => i.includes('record-id')).split('-').last().value()
    return recordId ? parseInt(recordId) : null
  } else return null
}

const setCellFocus = (tableId, cells, oldCell, newCell, rows) => {
  if(_.get(rows, oldCell.row)) _.each(rows[oldCell.row], row => row.style.backgroundColor = "inherit");
  if(_.get(cells, oldCell.row) && _.get(_.get(cells, oldCell.row), oldCell.column)) {
    cells[oldCell.row][oldCell.column].style.outline = "0";
    cells[oldCell.row][oldCell.column].blur()
  }
  _.each(getSystemDisabledCells(tableId), cell => cell.style.outline = "0")
  if(cells[newCell.row] && cells[newCell.row].length) {
    _.each(rows[newCell.row], row => row.style.backgroundColor = Colors.heatherGrey);
    cells[newCell.row][newCell.column].style.outline = `2px solid ${Colors.blackPearl}`;
    cells[newCell.row][newCell.column].style.outlineOffset = `-2px`;
    const visibility = cell_visibility(tableId, cells[newCell.row][newCell.column])

    if(!visibility.x_visible) {
      visibility.x_scrollable.scrollBy({left: visibility.x_offset, top: visibility.y_offset, behavior: 'auto'})
    }
    if(!visibility.y_visible) {
      visibility.y_scrollable.scrollBy({left: visibility.x_offset, top: visibility.y_offset, behavior: 'auto'})
    }
  }

  const isValidCell =
    newCell.row >= 0 &&
    newCell.row < cells.length &&
    newCell.column >= 0 &&
    newCell.column < (cells[newCell.row]?.length || 0)

  if(isValidCell) {
    const id = findRecordId(cells[newCell.row][newCell.column]?.parentElement)
    if(id) setContext(id)
  }

  updateValidation()
}

const removeCellFocus = (tableId, cells, cell) => {
  cells[cell.row][cell.column].style.outline = "0"
  cells[cell.row][cell.column].blur()
}

const focusCellByContent = (tableId, content, data?) => {
  window[`${tableId}_carousel_in_focus`] = false
  window[`${tableId}_table_lost_focus`] = false
  const rows = getRows(tableId)
  const cells = getCells(rows)
  _.each(cells, (r, i) => { // rows
    _.each(r, (c, j) => { // cells
      if(_.includes(c.textContent, content)) { // found cell with content
        const newCell = {row: i, column: j, init: false}
        setCellFocus(tableId, cells, cell.value, newCell, rows)
        cell.next(newCell)
        editing.next(false)
        return false
      }
    })
  })
}

const focusCellByDataId = (tableId, id, data?) => {
  window[`${tableId}_carousel_in_focus`] = false
  window[`${tableId}_table_lost_focus`] = false
  const rows = getRows(tableId)
  const cells = getCells(rows)
  let found = false
  _.each(cells, (r, i) => { // rows
    _.each(r, (c, j) => { // cells
      if(c.querySelector(`[data-id="${id}"]`)) { // found descendent with data-id
        const newCell = {row: i, column: j, init: false}
        setCellFocus(tableId, cells, cell.value, newCell, rows)
        cell.next(newCell)
        editing.next(false)
        found = true
        return false
      }
    })
  })
  return found
}

const focusFirstCell = (tableId, data?) => {
  window[`${tableId}_carousel_in_focus`] = false
  window[`${tableId}_table_lost_focus`] = false
  const rows = getRows(tableId)
  const cells = getCells(rows)
  let firstEditableRow: number | undefined = undefined
  for(let i = 0; i < cells.length; i++) {
    if(cells[i].length) {
      firstEditableRow = i
      break
    }
  }
  if(firstEditableRow !== undefined) {
    const newCell = {row: firstEditableRow, column: 0, init: false}
    setCellFocus(tableId, cells, cell.value, newCell, rows)
    cell.next(newCell)
    editing.next(false)
  }
}

const focusFirstCellByRowIndex = (tableId, rowIndex, data?) => {
  window[`${tableId}_carousel_in_focus`] = false
  window[`${tableId}_table_lost_focus`] = false
  const rows = getRows(tableId)
  const cells = getCells(rows)
  let firstEditableRow: number | undefined = undefined
  for(let i = rowIndex; i >= 0; i--) {
    if(cells[i] && cells[i].length) {
      firstEditableRow = i
      break
    }
  }
  if(firstEditableRow !== undefined) {
    const selectCheck = cells[firstEditableRow][0]
    const column = _.includes(selectCheck?.classList, 'p-selection-column') ? 1 : 0
    const newCell = {row: firstEditableRow, column, init: false}
    setCellFocus(tableId, cells, cell.value, newCell, rows)
    cell.next(newCell)
    editing.next(false)
  }
}

const focusLastCell = (tableId, data?) => {
  window[`${tableId}_carousel_in_focus`] = false
  window[`${tableId}_table_lost_focus`] = false
  const rows = getRows(tableId)
  const cells = getCells(rows)
  let firstEditableRow: number | undefined = undefined
  for(let i = cells.length; i >= 0; i--) {
    if(cells[i].length) {
      firstEditableRow = i
      break
    }
  }
  if(firstEditableRow !== undefined) {
    const newCell = {row: firstEditableRow, column: cells[firstEditableRow].length - 1, init: false}
    setCellFocus(tableId, cells, cell.value, newCell, rows)
    cell.next(newCell)
    editing.next(false)
  }
}

const unfocusAllCells = () => {
  _.invoke(document.elementFromPoint(0, 0), "click")
}

const findEditableParentCell = (ele: Element): Element => {
  if(ele.parentElement) {
    if(ele.parentElement.classList.contains("p-editable-column") ||
       ele.parentElement.classList.contains("p-selection-column")) return ele.parentElement
    else return findEditableParentCell(ele.parentElement)
  } else {
    return ele
  }
}

const findCarouselBtn = (cell: Element): Element | null => {
  if(cell.children && cell.children.length) {
    for(let i = 0; i < cell.children.length; i++) {
      let node = cell.children[i]
      if(_.includes(_.get(node, "className"), "carousel-button")) return node
      else return findCarouselBtn(node)
    }
  } else {
    return null
  }
  return null
}

const findDetailedRouteBtn = (cell: Element): Element | null => {
  if(cell.children && cell.children.length) {
    let node = cell.children[0]
    if(_.includes(_.get(node, "className"), "detailed-route-button")) return node
    else return null
  } else return null
}

const findChildCheckbox = (cell: Element): Element | null => {
  return cell.querySelector('input[type="checkbox"')
}

// paging
const pageTo = (tableId, pageNumber) => {
  window[`${tableId}_table_lost_focus`] = false
  const el = document.querySelector(`#${tableId} .p-paginator-page.p-paginator-element.p-link:nth-of-type(${pageNumber})`)
  if(el && _.get(el, "click")) _.invoke(el, "click")
  setTimeout(() => focusFirstCell(tableId), 300)
}

const pageToFirst = tableId => {
  const tableSelector = `#${tableId}`
  const pagerSelector = `${tableSelector} .p-paginator`
  const firstSelector = `${pagerSelector} .p-paginator-first`
  window[`${tableId}_table_lost_focus`] = false
  const el = document.querySelector(firstSelector)
  if(el && _.get(el, "click")) _.invoke(el, "click")
}

const pageToLast = tableId => {
  const tableSelector = `#${tableId}`
  const pagerSelector = `${tableSelector} .p-paginator`
  const lastSelector = `${pagerSelector} .p-paginator-last`
  window[`${tableId}_table_lost_focus`] = false
  const el = document.querySelector(lastSelector)
  if(el && _.get(el, "click")) _.invoke(el, "click")
}

const getPagingInfo = (tableId: string): {currentPage: number, pageSize: number, totalPages: number} => {
  let pageElem = document.querySelector(`#${tableId} .p-paginator-page.p-paginator-element.p-link.p-highlight`)
  let pageSizeElem = document.querySelector(`#${tableId} .p-paginator .p-dropdown-label.p-inputtext`)
  let lastPageElem = document.querySelector(`#${tableId} .p-paginator-page.p-paginator-element.p-link:last-of-type`)
  return {
    currentPage: _.parseInt(_.get(pageElem, "innerText")),
    pageSize: _.parseInt(_.get(pageSizeElem, "innerText")),
    totalPages: _.parseInt(_.get(lastPageElem, "innerText"))
  }
}

const getFocusedRowIndex = () => {
  const c = cell.getValue()
  if(c && c.row) return c.row
  else return 0
}

// keyboard navigation and mouse click synchronization
const TableAutomation = (props: ITableAutomation) => {
  // table selectors
  const [tableAutomationActive, setAutomationActive] = useState<boolean>(props.automationActive === undefined ? true : props.automationActive)
  const pagerSelector = `#${props.tableId} .p-paginator`
  const pagerKeydownSelector = `${pagerSelector} .p-dropdown .p-hidden-accessible input`

  useEffect(() => {
    cell.next({row: 0, column: 0, init: true})
  }, [props.tableId])

  useEffect(() => {
    const isActive = props.automationActive === undefined ? true : props.automationActive
    setAutomationActive(isActive)
    const rows = getRows(props.tableId)
    const cells = getCells(rows)
    if(isActive) {
      window[`${props.tableId}_table_lost_focus`] = false
      setCellFocus(props.tableId, cells, cell.value, cell.value, rows)
    } else {
      if(cells[cell.value.row]) {
        cells[cell.value.row][cell.value.column].style.outline = "0"
        _.each(rows[cell.value.row], row => row.style.backgroundColor = "inherit")
      }
      cell.next({row: 0, column: 0, init: false})
    }
  }, [props.automationActive])

  useEffect(() => {
      // initial load focus effect
    if(cell.value.init) {
      if(!tableAutomationActive || window[`${props.tableId}_table_lost_focus`] || window[`${props.tableId}_carousel_in_focus`]) return
      setlocal('tableid', props.tableId)
      const rows = getRows(props.tableId)
      const cells = getCells(rows)
      if(cells.length) {
        let firstEditableRow: number | undefined = undefined
        for(let i = 0; i < cells.length; i++) {
          if(cells[i].length) {
            firstEditableRow = i
            break
          }
        }
        if(firstEditableRow !== undefined) {
          const newCell = {row: firstEditableRow, column: 0, init: false}

          setCellFocus(props.tableId, cells, cell.value, newCell, rows)
          cell.next(newCell)
          editing.next(false)
        }
      }
    }
    // prevent pager from listening for keyboard events after mouse interaction
    const pagerKeydownListener = document.querySelector(pagerKeydownSelector)
    _.invoke(pagerKeydownListener, "parentNode.removeChild", pagerKeydownListener)
  }, [tableAutomationActive])

  // paging focus effect
  useEffect(() => {
    // this whole component is a hack, but a sort of charming one
    // except for this part - this is a dirty hack
    // i will accept any ideas for improvement
    const pager = document.querySelector(pagerSelector)
    const timeout = 300
    const focusFirstCellWithTimeout = e => setTimeout(() => {
      const ele = e.srcElement
      if(ele.className.includes("p-dropdown") &&
        !ele.className.includes("p-dropdown-item")) {
          return
      }
      window[`${props.tableId}_table_lost_focus`] = false
      const rows = getRows(props.tableId)
      const cells = getCells(rows)
      let firstEditableRow: number | undefined = undefined
      for(let i = 0; i < cells.length; i++) {
        if(cells[i].length) {
          firstEditableRow = i
          break
        }
      }
      if(firstEditableRow !== undefined) {
        const newCell = {row: firstEditableRow, column: 0, init: false}

        setCellFocus(props.tableId, cells, cell.value, newCell, rows)
        cell.next(newCell)
        editing.next(false)
      }
    }, timeout)

    _.invoke(pager, "addEventListener", "click", focusFirstCellWithTimeout)
    return () => {
      _.invoke(pager, "removeEventListener", "click", focusFirstCellWithTimeout)
    }
  }, [])

  function handleMultiselect(e, multiselectInput):void {
    if (e.target.classList.contains('p-multiselect-label') || e.target.classList.contains('p-multiselect-label-container') || e.target.classList.contains('p-multiselect-trigger-icon')) return
    e.preventDefault()
    let children = Array.prototype.slice.call(multiselectInput.querySelectorAll('.p-checkbox-box'));
    let selectedChild:any = null
    for (let i of children){
      if (i.style.outlineWidth == '2px') selectedChild = i
    }
    if(_.includes(["ArrowUp", "ArrowDown"], e.key)) {
      let currentIdx = 0;
      if (selectedChild){
        selectedChild.style.outline = '0'
        currentIdx = e.key === 'ArrowUp' ? children.indexOf(selectedChild) - 1 : children.indexOf(selectedChild) + 1
      }
      if (currentIdx === children.length || currentIdx === -1) currentIdx = 0
      children[currentIdx].style.outline = '2px solid #082141'
    } else if (_.includes(["Space"], e.code)){
      if (selectedChild) selectedChild.click()
    } else {
      const child = e.target.closest('.p-checkbox-box');
      child.click()
      if (selectedChild) selectedChild.style.outline = '0'
      else child.style.outline = '2px solid #082141'
    }
  }

  // primereact key handler overrides, custom keyboard event handlers, user mouse-click sync effects
  useEffect(() => {
    const overrideKeyHandlers = e => {
      // disable keyboard scroll control and native tabbing
      const isMultiselectInput = e.target.closest('.multiselect-input');
      // Handle multiselect input (Chart summary accounts property type as example)
      if (isMultiselectInput){
        handleMultiselect(e, isMultiselectInput);
        return
      }
      if(_.includes(["ArrowUp", "ArrowDown", "Tab", "Enter"], e.key)) {
        if(!tableAutomationActive || window[`${props.tableId}_table_lost_focus`] || window[`${props.tableId}_carousel_in_focus`]) return
        e.preventDefault()
        return false
      }
      // disable keyboard scroll control
      if(props.automationActive
      && !editing.value
      && !_.includes(_.get(e, "target.parentElement.className"), "p-filter-column")
      && _.includes(["ArrowLeft", "ArrowRight"], e.key)) {
        e.preventDefault()
        return false
      }
      // disable spacebar default for the top level table
      if(!editing.value && _.includes(["Space"], e.code)) {
        if(!tableAutomationActive || window[`${props.tableId}_table_lost_focus`] || window[`${props.tableId}_carousel_in_focus`]) return
        e.preventDefault()
        return false
      }
      // disable browser zoom on hotkey add/delete
      if(e.ctrlKey && _.includes(["_", "=", "+", "-", "Add", "Subtract"], e.key)) {
        e.preventDefault()
        return false
      }
      // close primereact dropdowns on esc if any are open
      if(_.includes(["Escape"], e.key)) {
        let prOpenDropdowns = document.querySelectorAll("div.p-dropdown-panel.p-hidden.p-input-overlay.p-input-overlay-visible")
        _.each(prOpenDropdowns, dd => {
          let trigger = _.find(dd.parentElement.children, ele => ele.className === "p-dropdown-trigger")
          _.invoke(trigger, "click")
        })
      }
      return true
    }

    const keySubscription = keyService.stream().subscribe(k => {
      if(!tableAutomationActive || window[`${props.tableId}_table_lost_focus`] || window[`${props.tableId}_carousel_in_focus`]) return

      let newCell: Cell = {row: 0, column: 0, init: false}
      const rows = getRows(props.tableId)
      const cells = getCells(rows)

      switch(true) {
        case k.chord === chord.LEFT && k.down === true:
          if(!tableAutomationActive || window[`${props.tableId}_carousel_in_focus`]) return
          newCell = directionalCellLocator(cells, Direction.Left)
          if(!editing.value && checkBounds(cells, newCell)) {
            setCellFocus(props.tableId, cells, cell.value, newCell, rows)
            cell.next(newCell)
          }
          break
        case k.chord === chord.RIGHT && k.down === true:
          if(!tableAutomationActive || window[`${props.tableId}_carousel_in_focus`]) return
          newCell = directionalCellLocator(cells, Direction.Right)
          if(!editing.value && checkBounds(cells, newCell)) {
            setCellFocus(props.tableId, cells, cell.value, newCell, rows)
            cell.next(newCell)
          }
          break
        case k.chord === chord.UP && k.down === true:
          if(!tableAutomationActive || window[`${props.tableId}_carousel_in_focus`]) return
          newCell = directionalCellLocator(cells, Direction.Up)
          if(!editing.value && checkBounds(cells, newCell)) {
            setCellFocus(props.tableId, cells, cell.value, newCell, rows)
            cell.next(newCell)
          }
          break
        case k.chord === chord.DOWN && k.down === true:
          if(!tableAutomationActive || window[`${props.tableId}_carousel_in_focus`]) return
          newCell = directionalCellLocator(cells, Direction.Down)
          if(!editing.value && checkBounds(cells, newCell)) {
            setCellFocus(props.tableId, cells, cell.value, newCell, rows)
            cell.next(newCell)
          }
          break
        case k.chord === chord.TAB && k.down === true:
          if(!tableAutomationActive || window[`${props.tableId}_carousel_in_focus`]) return
          newCell = directionalCellLocator(cells, Direction.Right)
          if(newCell.row === -1) return
          setCellFocus(props.tableId, cells, cell.value, newCell, rows)
          editing.next(false)
          cell.next(newCell)
          break
        case k.chord === chord.SHIFT_TAB && k.down === true:
          if(!tableAutomationActive || window[`${props.tableId}_carousel_in_focus`]) return
          newCell = directionalCellLocator(cells, Direction.Left)
          if(newCell.row === -1) return
          setCellFocus(props.tableId, cells, cell.value, newCell, rows)
          editing.next(false)
          cell.next(newCell)
          break
        case k.chord === chord.SPACE && k.down === true:
          if(!tableAutomationActive || window[`${props.tableId}_carousel_in_focus`] || editing.value) return
          const checkbox = findChildCheckbox(cells[cell.value.row][cell.value.column])
          if(checkbox) {
            _.invoke(checkbox, "click")
            setTimeout(() => {
              editing.next(false)
              setCellFocus(props.tableId, cells, cell.value, cell.value, rows)
            }, 1)
          }
          break
        case k.chord === chord.ENTER && k.down === true:
          if(!tableAutomationActive || window[`${props.tableId}_carousel_in_focus`]) return
          if(editing.value) {
            editing.next(false)
            setCellFocus(props.tableId, cells, cell.value, cell.value, rows)
          } else {
            editing.next(true)
            cells[cell.value.row][cell.value.column].style.outline = "0"
            const carousel = findCarouselBtn(cells[cell.value.row][cell.value.column])
            const detailedRoute = findDetailedRouteBtn(cells[cell.value.row][cell.value.column])
            if(carousel) {
              let trigger = new MouseEvent("dblclick", {
                "view": window,
                "bubbles": true,
                "cancelable": true
              })
              carousel.dispatchEvent(trigger) // open carousel
              window[`${props.tableId}_carousel_in_focus`] = true
            }
            else if (detailedRoute && detailedRoute instanceof HTMLElement) {
              detailedRoute.click()
            }
            else {
              cells[cell.value.row][cell.value.column].click() // open normal editor
            }
          }
          break;
        case k.chord === chord.ESC && k.down === true:
          if(editing.value) {
            window[`${props.tableId}_carousel_in_focus`] = false
            editing.next(false)
            setCellFocus(props.tableId, cells, cell.value, cell.value, rows)
          }
          break
        case k.chord === chord.CTRL_ALT_E: // user hotkeyed to the property selector
          editing.next(false)
          removeCellFocus(props.tableId, cells, cell.value)
          window[`${props.tableId}_table_lost_focus`] = true
          break
        }
    })

    const automationSubscription = automationService.stream().subscribe(event => {
      const rows = getRows(props.tableId)
      const cells = getCells(rows)
      let newCell
      switch(true) {
        case event.type === "REGISTER":
          if(automationid === event.data) {
            window[`${props.tableId}_table_lost_focus`] = false
            setAutomationActive(true)
          } else {
            window[`${props.tableId}_table_lost_focus`] = true
            setAutomationActive(false)
          }
          break
        case event.type === "MODAL_OPENING":
          editing.next(true)
          cells[cell.value.row][cell.value.column].style.outline = "0"
          window[`${props.tableId}_carousel_in_focus`] = true
          updateValidation()
          break
        case event.type === "MODAL_CLOSING":
          if(editing.value) {
            window[`${props.tableId}_carousel_in_focus`] = false
            editing.next(false)
            setCellFocus(props.tableId, cells, cell.value, cell.value, rows)
          }
          break
        case event.type === "MODAL_TABBING":
          window[`${props.tableId}_carousel_in_focus`] = false
          window[`${props.tableId}_table_lost_focus`] = false
          setAutomationActive(true)
          newCell = directionalCellLocator(cells, Direction.Right)
          setCellFocus(props.tableId, cells, cell.value, newCell, rows)
          editing.next(false)
          cell.next(newCell)
          break
        case event.type === "MODAL_SHIFT_TABBING":
          window[`${props.tableId}_carousel_in_focus`] = false
          window[`${props.tableId}_table_lost_focus`] = false
          setAutomationActive(true)
          newCell = directionalCellLocator(cells, Direction.Left)
          setCellFocus(props.tableId, cells, cell.value, newCell, rows)
          editing.next(false)
          cell.next(newCell)
          break
        case event.type === "SEARCHING":
          cell.next({row: cell.value.row, column: cell.value.column, init: false})
          break
        case event.type === "NEW_PROPERTY_MODAL_OPENING":
          window[`${props.tableId}_carousel_in_focus`] = true
          let oldCell = _.get(cells, `[${cell.value.row}][${cell.value.column}]`)
          if(oldCell) {
            oldCell.style.outline = "0"
            oldCell.blur()
          }
          editing.next(true)
          break
        case event.type === "NEW_PROPERTY_MODAL_CLOSING":
          window[`${props.tableId}_carousel_in_focus`] = false
          editing.next(false)
          break
      }
    })

    const clickSync = e => {
      e.stopPropagation()
      const isButton = e.target.classList.contains('cell-trigger-btn');
      if(!e.isTrusted || window[`${props.tableId}_carousel_in_focus`]) return
      const rows = getRows(props.tableId)
      const cells = getCells(rows)

      // Ignore clicks on on detailed route record caret up/down icons
      const detailedRouteRecordDown = document.getElementById('detailedRouteRecordDown');
      const detailedRouteRecordUp = document.getElementById('detailedRouteRecordUp')
      if (e.target === detailedRouteRecordDown || e.target === 'detailedRouteRecordUp' || detailedRouteRecordUp?.contains(e.target) || detailedRouteRecordDown?.contains(e.target)) {
        return
      }

      if(cell_clicked(props.tableId, e)) {
        automationService.broadcast("REGISTER", automationid)
        const path = e.path || (e.composedPath && e.composedPath())
        let clickedCell: undefined | Element = undefined
        if(path) clickedCell = _.find(path, el => _.includes(el.className, "p-editable-column") || _.includes(el.className, "p-selection-column"))
        else clickedCell = findEditableParentCell(e.currentTarget)
        _.each(cells, (r, i) => { // rows
          _.each(r, (c, j) => { // cells
            if(_.isEqual(clickedCell, c)) { // found clicked cell
              const newCell = {row: i, column: j, init: false}
              if(e.type === "contextmenu") {
                setCellFocus(props.tableId, cells, cell.value, newCell, rows)
                cell.next(newCell)
                editing.next(false)
              } else {
                setCellFocus(props.tableId, cells, cell.value, newCell, rows)
                cell.next(newCell)
                const carousel = findCarouselBtn(cells[newCell.row][newCell.column])
                if(carousel && !isButton) editing.next(false)
                else editing.next(true)
              }
              return false
            }
          })
        })
      } else {
        window[`${props.tableId}_table_lost_focus`] = true
        let oldCell = _.get(cells, `[${cell.value.row}][${cell.value.column}]`)
        if(oldCell) {
          oldCell.style.outline = "0"
          oldCell.blur()
        }
        automationService.broadcast("UNREGISTER", automationid)
        editing.next(false)
      }
    }

    // start editing in overwrite mode
    function overwrite(evt) {
      if(!tableAutomationActive || window[`${props.tableId}_table_lost_focus`] || window[`${props.tableId}_carousel_in_focus`]) return

      let newCell: Cell = {row: 0, column: 0, init: false}
      const rows = getRows(props.tableId)
      const cells = getCells(rows)

      if(!editing.value) {
        evt = evt || window.event
        var charCode = evt.which || evt.keyCode
        var charStr = String.fromCharCode(charCode)
        if (!(evt.ctrlKey && evt.altKey) &&
            !(evt.shiftKey && evt.altKey && (charCode === 90 || charCode === 82)) &&
            charCode !== 13 && charCode !== 9 && charStr !== "" && /[a-z0-9`\s\b]/i.test(charStr)) {
          editing.next(true)
          cells[cell.value.row][cell.value.column].style.outline = "0"
          cells[cell.value.row][cell.value.column].click() // open editor
          let input = _.find(cells[cell.value.row][cell.value.column].children, (c) => c.nodeName === "INPUT")
          input && input.select()
        }
      }
    }

    document.addEventListener("keydown", overrideKeyHandlers)
    document.addEventListener("click", clickSync)
    document.addEventListener("contextmenu", clickSync)
    document.addEventListener("keydown", overwrite)

    //document.addEventListener("contextmenu", clickSync)
    return () => {
      keySubscription.unsubscribe()
      automationSubscription.unsubscribe()
      document.removeEventListener("keydown", overrideKeyHandlers)
      document.removeEventListener("click", clickSync)
      document.removeEventListener("contextmenu", clickSync)
      document.removeEventListener("keydown", overwrite)

      //document.addEventListener("contextmenu", clickSync)
    }
  }, [cell, editing, tableAutomationActive])

  const checkBounds = (cells, newCell) => {
    return !(
      newCell.row >= cells.length ||
      newCell.row < 0 ||
      newCell.column >= cells[newCell.row].length ||
      newCell.column < 0
    )
  }

  const directionalCellLocator = (cells, direction: Direction) => {
    const c = cell.value
    let newCell: Cell = {row: 0, column: 0, init: false}

    if(direction === Direction.Right) {
      if(c.column + 1 >= cells[c.row].length) {
        let newRow = cells.length
        for(let i = c.row + 1; i < cells.length; i++) {
          if(cells[i].length) {
            newRow = i
            break
          }
        }
        if(newRow >= cells.length) {
          if(props.tabOut) {
            props.tabOut()
            removeCellFocus(props.tableId, cells, c)
            return {row: -1, column: -1, init: false}
          }
          let pager = getPagingInfo(props.tableId)
          if(pager.currentPage === pager.totalPages) {
            if(!editing.value) pageTo(props.tableId, 1)
          } else {
            if(!editing.value) pageTo(props.tableId, pager.currentPage + 1)
          }
        } else {
          newCell = {row: newRow, column: 0, init: false}
        }
      } else {
        newCell = {row: c.row, column: c.column + 1, init: false}
      }
    } else if(direction === Direction.Left){
      if(c.column - 1 < 0) {
        let newRow = -1
        for(let i = c.row - 1; i >= 0; i--) {
          if(cells[i].length) {
            newRow = i
            break
          }
        }
        if(newRow < 0) {
          if(props.shiftTabOut) {
            props.shiftTabOut()
            removeCellFocus(props.tableId, cells, c)
            return {row: -1, column: -1, init: false}
          }
          else {
            let pager = getPagingInfo(props.tableId)
            if(pager.currentPage === 1) {
              if(!editing.value) pageTo(props.tableId, pager.totalPages)
            } else {
              if(!editing.value) pageTo(props.tableId, pager.currentPage - 1)
            }
          }
        } else {
          newCell = {row: newRow, column: cells[newRow].length - 1, init: false}
        }
      } else {
        newCell = {row: c.row, column: c.column - 1, init: false}
      }
    } else if(direction === Direction.Up) {
      if(c.row - 1 < 0) {
        let newRow = cells.length - 1
        for(let i = cells.length - 1; i >= 0; i--) {
          if(cells[i].length) {
            newRow = i
            break
          }
        }
        if(cells[newRow].length === cells[c.row].length) {
          newCell = {row: newRow, column: c.column, init: false}
        } else {
          newCell = {row: newRow, column: cells[cells.length - 1].length - 1, init: false}
        }
      } else {
        let newRow = c.row - 1
        for(let i = c.row - 1; i >= 0; i--) {
          if(cells[i].length) {
            newRow = i
            break
          }
        }
        if(cells[newRow].length === cells[c.row].length) {
          newCell = {row: newRow, column: c.column, init: false}
        } else {
          newCell = {row: newRow, column: cells[newRow].length - 1, init: false}
        }
      }
    } else if(direction === Direction.Down) {
      if(c.row + 1 >= cells.length) {
        let newRow = 0
        for(let i = 0; i < cells.length; i++) {
          if(cells[i].length) {
            newRow = i
            break
          }
        }
        if(cells[newRow].length === cells[c.row].length) {
          newCell = {row: newRow, column: c.column, init: false}
        } else {
          newCell = {row: newRow, column: 0, init: false}
        }
      } else {
        let newRow = c.row + 1
        for(let i = c.row + 1; i < cells.length; i++) {
          if(cells[i].length) {
            newRow = i
            break
          }
        }
        if(cells[newRow].length === cells[c.row].length) {
          newCell = {row: newRow, column: c.column, init: false}
        } else {
          newCell = {row: newRow, column: 0, init: false}
        }
      }
    }

    return newCell
  }

  return null
}

const updateValidation = _.debounce(() => {
  Array.from(document.querySelectorAll('.has-errors')).forEach(el => el.classList.remove('has-errors'))
  Array.from(document.querySelectorAll('[data-error]')).forEach(el => {
    const td = (function getPrimeReactTD(el){
      if (!el || el === document.body) return
      if (el.classList.contains('automated')) return el
      const parent = el.parentElement
      if (parent && parent.nodeName === 'TR') return el
      return getPrimeReactTD(parent!)
    }(el))
    if (td)
      return td.classList.add('has-errors')
    const child = el.children.length == 1 && el.children[0]
    if (el.tagName == 'SPAN' && child && child.tagName == 'DIV')
      return child.classList.add('has-errors')

    el.classList.add('has-errors')
  })
}, 300)


const rowContextService = {
  stream: () => rowContextStream.asObservable()
}

// public api
export {
  TableAutomation,
  focusCellByContent,
  focusCellByDataId,
  focusFirstCell,
  focusFirstCellByRowIndex,
  unfocusAllCells,
  getPagingInfo,
  getFocusedRowIndex,
  pageTo,
  pageToFirst,
  pageToLast,
  updateValidation,
  rowContextService,
}

